
import { BlockComponentProps } from 'client-react-block-renderer';
import React, { useCallback, useMemo, useState } from 'react';
import ProductRows from './ProductRows';
import Sidebar from './Sidebar';
import './style.scss';
import { CategoryData, ProductData } from './types';
import { useSearchResults } from './useSearchResults';
import t from 'rcm-i18n';
import { GtagSearchType } from 'google-analytics/src';

interface Config { }

interface Data {
    products: ProductData[];
    categories: CategoryData[];
}

type Props = BlockComponentProps<Data, Config>;

const AllProductsContainer = (props: Props) => {
    const allProductsCategoryId = useMemo(
        () => props.data.categories.find(category => category.name === 'All Products')?.id || null,
        [props.data.categories]
    );
    const sortedCategories = useMemo(
        () => props.data.categories.sort((a: CategoryData, b: CategoryData) => {
            if (a.id === allProductsCategoryId) {
                return -Infinity; // "All Products" category always goes on top
            }
            if (b.id === allProductsCategoryId) {
                return Infinity; // "All Products" category always goes on top
            }
            return a.name.localeCompare(b.name);
        }),
        [props.data.categories, allProductsCategoryId]
    );
    const [selectedCategoryId, setSelectedCategoryId] = useState(allProductsCategoryId);
    const [searchText, setSearchText] = useState('');
    const handleCategorySelect = useCallback((categoryId: number) => {
        setSearchText('');
        setSelectedCategoryId(categoryId);

        const category = sortedCategories.find((c) => c.id === categoryId);
        /**
         * @todo 2021-04-23 - bharms@relivinc.com - axosoft/25604:
         *       Seeing as this doesn't require a server call, it is not
         *       strictly necessary to move this to the server. But it does
         *       require that GA session tracking be coordinated with the
         *       server.
         */
        gtag('event', 'search', {
            search_type: GtagSearchType.ALL_PRODUCTS_CATEGORY,
            search_term: category.name,
        });
    }, [sortedCategories]);
    const handleSearchTextChange = useCallback((newSearchText: string) => {
        setSelectedCategoryId(allProductsCategoryId);
        setSearchText(newSearchText);

        /**
         * @todo 2021-04-23 - bharms@relivinc.com - axosoft/25604:
         *       Seeing as this doesn't require a server call, it is not
         *       strictly necessary to move this to the server. But it does
         *       require that GA session tracking be coordinated with the
         *       server.
         */
        gtag('event', 'search', {
            search_type: GtagSearchType.ALL_PRODUCTS_TERM,
            search_term: newSearchText,
        });
    }, [allProductsCategoryId]);
    const selectedCategory = useMemo(
        () => props.data.categories.find(category => category.id === selectedCategoryId) || null,
        [selectedCategoryId, props.data.categories]
    );
    const filteredProducts = useSearchResults({ searchText, selectedCategory }, props.data.products, props.data.categories);

    let searchDescription: string;
    if (searchText.length !== 0) {
        searchDescription = searchText;
    } else if (selectedCategoryId !== null) {
        searchDescription = selectedCategory.name
    } else {
        searchDescription = ''; // This should never happen if the "All Products" category exists.
    }

    if (allProductsCategoryId === null) {
        return <div className="alert alert-warning">
            <p><strong>{t('An error occured.')}</strong></p>
            <p>{t('Error Code')}: ALL_PRODUCTS_CATEGORY_MUST_EXIST_AND_BE_PUBLIC</p>
        </div>;
    }

    return <div className="all-products fluid-container">
        <div className="all-products-main-container">
            <div className="all-products-main-row">
                <div className="all-products-sidebar-wrapper">
                    <Sidebar categories={sortedCategories}
                        selectedCategoryId={selectedCategoryId}
                        onCategorySelect={handleCategorySelect}
                        searchText={searchText}
                        onSearchTextChange={handleSearchTextChange}
                        searchDescription={searchDescription}
                        filteredProducts={filteredProducts} />
                </div>
                <div className="all-products-product-grid-wrapper">
                    <ProductRows products={filteredProducts} />
                </div>
            </div>
        </div>
    </div>
}

export default AllProductsContainer;
